import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { api, type HotDeal, type HotDealsSnapshot, type PriceCheckResult } from "../lib/api";

type VerdictFilter = "actionable" | "STRONG_BUY" | "BUY" | "WATCH";
type GradeFilter = "all" | "10" | "9" | "8" | "7";
type GameFilter = "all" | "pokemon" | "onepiece";

type SortKey = "card" | "bin" | "vs_sold" | "edge" | "liq" | "seller";
type SortDir = "asc" | "desc";

const LIQ_RANK: Record<string, number> = { hot: 3, warm: 2, cold: 1, unknown: 0 };

// Button label while a search is running — shows scored/total once discovery lands.
function searchingLabel(p: { done: number; total: number } | null): string {
  return p && p.total > 0 ? `searching ${p.done}/${p.total}…` : "searching…";
}

// Comparable value per sortable column. Numbers sort numerically; card sorts by name.
function sortValue(d: HotDeal, key: SortKey): number | string {
  switch (key) {
    case "card": return `${d.card_name} ${d.card_number}`.toLowerCase();
    case "bin": return d.listing_total;
    case "vs_sold": return d.sold_median;
    case "edge": return d.raw_gap_pct;
    case "liq": return LIQ_RANK[d.liquidity] ?? 0;
    case "seller": return d.seller_fb ?? -1;
  }
}

export default function HotDeals() {
  const [snap, setSnap] = useState<HotDealsSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<VerdictFilter>("actionable");
  const [gradeFilter, setGradeFilter] = useState<GradeFilter>("all");
  const [gameFilter, setGameFilter] = useState<GameFilter>("all");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "edge", dir: "desc" });
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const pollRef = useRef<number | null>(null);
  const baselineRef = useRef<string | null>(null);

  function stopPolling(): void {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = null;
  }

  // Poll the server's status until the running refresh finishes, then swap in the
  // fresh snapshot. Works whether the run was started here or in another tab/before
  // a reload — the "running" flag lives on the server, so a page reload re-attaches.
  function startPolling(baseline: string | null): void {
    baselineRef.current = baseline;
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const st = await api.getHotDealsStatus();
        setProgress(st.progress);
        const finished = !st.running && st.refreshed_at !== baselineRef.current;
        if (finished) {
          const s = await api.getHotDeals();
          if (s) setSnap(s);
          setRefreshing(false);
          setProgress(null);
          stopPolling();
        }
      } catch {
        /* transient — keep polling */
      }
    }, 5_000);
  }

  // Click a header: first click sorts (numeric desc / card asc), re-click toggles direction.
  function toggleSort(key: SortKey): void {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "card" ? "asc" : "desc" }));
  }

  async function load(): Promise<void> {
    try {
      const [s, st] = await Promise.all([api.getHotDeals(), api.getHotDealsStatus()]);
      setSnap(s);
      setError(null);
      // A search may already be running (started in another tab or before this
      // reload) — re-show "searching" and re-attach the poller.
      if (st.running) {
        setRefreshing(true);
        setProgress(st.progress);
        startPolling(st.refreshed_at);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    return () => stopPolling();
  }, []);

  async function refresh(): Promise<void> {
    setRefreshing(true);
    setProgress(null);
    try {
      const baseline = snap?.refreshed_at ?? null;
      await api.refreshHotDeals();
      startPolling(baseline);
    } catch (e) {
      setError(String(e));
      setRefreshing(false);
    }
  }

  const filtered = useMemo<HotDeal[]>(() => {
    if (!snap) return [];
    let rows = filter === "actionable"
      ? snap.deals.filter((d) => d.verdict === "STRONG_BUY" || d.verdict === "BUY" || d.verdict === "WATCH")
      : snap.deals.filter((d) => d.verdict === filter);
    if (gradeFilter !== "all") rows = rows.filter((d) => d.grade === gradeFilter);
    if (gameFilter !== "all") rows = rows.filter((d) => d.game === gameFilter);
    const mult = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (typeof av === "string" || typeof bv === "string") {
        return mult * String(av).localeCompare(String(bv));
      }
      return mult * (av - bv);
    });
  }, [snap, filter, gradeFilter, gameFilter, sort]);

  if (loading) return <div className="p-6 text-zinc-500 font-mono">…</div>;

  if (!snap) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <h1 className="text-2xl font-mono text-zinc-100 mb-3">hot deals</h1>
        <p className="text-zinc-400 mb-6 text-sm">
          Discovers active Pokemon PSA-graded cards from live eBay sold flow (no watchlist input),
          then scores every BIN listing against real recent sold comps at the same PSA grade.
        </p>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="px-4 py-2 bg-zinc-100 text-zinc-900 font-mono text-sm rounded hover:bg-white disabled:opacity-50"
        >
          {refreshing ? searchingLabel(progress) : "run first discovery"}
        </button>
        {error && <p className="mt-4 text-red-400 text-sm font-mono">{error}</p>}
        <div className="mt-8">
          <p className="text-zinc-400 text-sm mb-2">Or check the price of a specific card right now:</p>
          <PriceCheckBar />
        </div>
      </div>
    );
  }

  const counts = snap.counts;
  const fresh = new Date(snap.refreshed_at);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-baseline justify-between mb-5">
        <h1 className="text-2xl font-mono text-zinc-100">hot deals</h1>
        <div className="flex gap-3 items-center text-xs font-mono text-zinc-500">
          <span>
            refreshed {fresh.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            {" · "}
            {snap.duration_seconds}s
          </span>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="px-3 py-1 border border-zinc-700 rounded hover:border-zinc-500 text-zinc-300 disabled:opacity-50"
          >
            {refreshing ? searchingLabel(progress) : "refresh"}
          </button>
        </div>
      </div>

      <p className="text-zinc-400 text-sm mb-6 max-w-3xl">
        Discovered {snap.candidates.length} English PSA-graded cards (Pokémon + One Piece, PSA 10/9/8/7) from live eBay sold flow.
        Every BIN is scored against its OWN same-grade comps (PSA 9 vs PSA 9, never mixed), seller feedback is confirmed &gt; 0, and
        the slab photo is vision-verified as the right PSA card before it's shown. Foreign-language listings excluded.
      </p>
      {snap.coverage && (
        <p className="text-xs font-mono text-zinc-600 mb-6">
          coverage this run: checked <span className="text-zinc-400">{snap.coverage.cards_checked}</span> cards ·
          {" "}<span className="text-zinc-400">{snap.coverage.listings_examined}</span> live listings examined ·
          {" "}by game {Object.entries(snap.coverage.by_game).map(([g, n]) => `${g === "onepiece" ? "One Piece" : "Pokémon"} ${n}`).join(", ")} ·
          {" "}by grade {Object.entries(snap.coverage.by_grade).sort().map(([g, n]) => `PSA${g} ${n}`).join(", ")}
          {" — "}most are at/above market (SKIP); only edges are shown below.
        </p>
      )}

      <PriceCheckBar />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat n={counts.strong_buy} label="strong buy" color="green" />
        <Stat n={counts.buy} label="buy" color="green" />
        <Stat n={counts.watch} label="watch" color="amber" />
        <Stat n={counts.skip} label="skipped" color="zinc" />
        <Stat n={snap.candidates.length} label="cards" color="zinc" />
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {(["actionable", "STRONG_BUY", "BUY", "WATCH"] as VerdictFilter[]).map((v) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            className={`px-3 py-1 text-xs font-mono rounded border ${
              filter === v ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            {v === "actionable" ? "all actionable" : v.toLowerCase().replace("_", " ")}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-3 items-center flex-wrap">
        <span className="text-xs font-mono text-zinc-600 mr-1">grade:</span>
        {(["all", "10", "9", "8", "7"] as GradeFilter[]).map((g) => (
          <button
            key={g}
            onClick={() => setGradeFilter(g)}
            className={`px-3 py-1 text-xs font-mono rounded border ${
              gradeFilter === g ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            {g === "all" ? "all grades" : `PSA ${g}`}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <span className="text-xs font-mono text-zinc-600 mr-1">game:</span>
        {([["all","all games"],["pokemon","Pokémon"],["onepiece","One Piece"]] as [GameFilter,string][]).map(([g,label]) => (
          <button
            key={g}
            onClick={() => setGameFilter(g)}
            className={`px-3 py-1 text-xs font-mono rounded border ${
              gameFilter === g ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="border border-zinc-800 rounded overflow-hidden">
        <table className="w-full text-sm font-mono">
          <thead className="bg-zinc-900 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2 w-8">#</th>
              <SortTh label="card" col="card" align="left" sort={sort} onSort={toggleSort} />
              <SortTh label="BIN" col="bin" align="right" sort={sort} onSort={toggleSort} />
              <SortTh label="vs sold" col="vs_sold" align="right" sort={sort} onSort={toggleSort} />
              <SortTh label="edge" col="edge" align="right" sort={sort} onSort={toggleSort} />
              <SortTh label="liq" col="liq" align="center" sort={sort} onSort={toggleSort} />
              <SortTh label="seller" col="seller" align="center" sort={sort} onSort={toggleSort} />
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d, i) => (
              <DealRow key={d.listing_id} deal={d} rank={i + 1} />
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-8 text-zinc-500">
                  no listings in this bucket
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="text-xs text-zinc-600 px-3 py-2 border-t border-zinc-800">
          Click any <span className="text-zinc-400">$ vs sold</span> value to audit the exact sold comps behind the median.
        </p>
      </div>
    </div>
  );
}

function SortTh({
  label, col, align, sort, onSort,
}: {
  label: string;
  col: SortKey;
  align: "left" | "right" | "center";
  sort: { key: SortKey; dir: SortDir };
  onSort: (k: SortKey) => void;
}) {
  const active = sort.key === col;
  const alignCls = align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center";
  const justify = align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center";
  return (
    <th className={`${alignCls} px-3 py-2`}>
      <button
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 uppercase hover:text-zinc-200 ${active ? "text-zinc-200" : "text-zinc-500"} ${justify}`}
        title={`Sort by ${label}`}
      >
        {label}
        <span className="text-[9px] w-2">{active ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
      </button>
    </th>
  );
}

function Metric({ label, value, sub, tone = "neutral" }: { label: string; value: string; sub?: string; tone?: "good" | "bad" | "neutral" }) {
  const c = tone === "good" ? "text-emerald-400" : tone === "bad" ? "text-red-400" : "text-zinc-200";
  return (
    <div className="border border-zinc-800 rounded px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm font-mono ${c}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function Stat({ n, label, color }: { n: number; label: string; color: "green" | "amber" | "zinc" }) {
  const c = color === "green" ? "text-emerald-400" : color === "amber" ? "text-amber-400" : "text-zinc-200";
  return (
    <div className="border border-zinc-800 rounded p-3">
      <div className={`text-2xl font-mono ${c}`}>{n}</div>
      <div className="text-xs uppercase text-zinc-500 tracking-wider mt-1">{label}</div>
    </div>
  );
}

function DealRow({ deal, rank }: { deal: HotDeal; rank: number }) {
  const [open, setOpen] = useState(false);
  const edgeColor =
    deal.verdict === "STRONG_BUY" ? "text-emerald-400" : deal.verdict === "BUY" ? "text-emerald-500" : "text-amber-400";
  const borderColor =
    deal.verdict === "STRONG_BUY" || deal.verdict === "BUY" ? "border-l-emerald-500" : "border-l-amber-500";
  const liqColor =
    deal.liquidity === "hot" ? "bg-emerald-900/40 text-emerald-300" :
    deal.liquidity === "warm" ? "bg-amber-900/40 text-amber-300" :
    deal.liquidity === "cold" ? "bg-red-900/40 text-red-300" : "bg-zinc-800 text-zinc-500";
  const graderLabel = deal.grader && deal.grade ? `${deal.grader} ${deal.grade}` : "raw";
  const setLabel = deal.card_set ?? "";
  const fbStr = deal.seller_fb != null ? deal.seller_fb.toLocaleString() : "?";
  const audit = deal.sold_audit;
  return (
    <Fragment>
      <tr className={`border-b border-zinc-800/60 border-l-2 ${borderColor} hover:bg-zinc-900/40`}>
        <td className="px-3 py-2 text-zinc-500 text-xs">{rank}</td>
        <td className="px-3 py-2">
          <a href={deal.listing_url} target="_blank" rel="noopener" className="text-zinc-100 hover:text-white">
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded mr-1.5 ${deal.game === "onepiece" ? "bg-red-900/50 text-red-300" : "bg-yellow-900/40 text-yellow-300"}`}
            >
              {deal.game === "onepiece" ? "OP" : "PKM"}
            </span>
            {deal.card_name} {deal.card_number.includes("/") ? "#" + deal.card_number.split("/")[0] : deal.card_number}{" "}
            <span className="text-xs px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-300 ml-1">{graderLabel}</span>
          </a>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            {setLabel && <span>{setLabel} · </span>}
            <span>{deal.sold_count_30d} sold /30d</span>
            {deal.active_bins > 1 ? (
              <span className="text-emerald-500" title={`Cheapest of ${deal.active_bins} active BINs we found for this card`}>
                ↓ lowest of {deal.active_bins}
              </span>
            ) : (
              <span className="text-zinc-600" title="Only active BIN we found for this card">sole listing</span>
            )}
            {deal.trend_dir === "down" && (
              <span className="text-red-400" title={`Recent sales ${deal.trend_pct}% vs prior 60d`}>▼ trending down {Math.abs(deal.trend_pct ?? 0)}%</span>
            )}
            {deal.trend_dir === "up" && (
              <span className="text-emerald-400" title={`Recent sales +${deal.trend_pct}% vs prior 60d`}>▲ trending up {deal.trend_pct}%</span>
            )}
            {deal.image_verdict === "KEEP" && (
              <span className="text-emerald-500" title={deal.image_detail ?? ""}>✓ slab verified</span>
            )}
            {deal.psa_confirmed && (
              <a
                href={deal.psa_cert ? `https://www.psacard.com/cert/${deal.psa_cert}` : undefined}
                target="_blank"
                rel="noopener"
                className="text-sky-400 hover:text-sky-300"
                title={`PSA cert ${deal.psa_cert}${deal.psa_set ? " · " + deal.psa_set : ""}`}
              >
                ✓ PSA cert
              </a>
            )}
            {deal.image_verdict === "UNCERTAIN" && (
              <span className="text-amber-500" title={deal.image_detail ?? ""}>image unverified</span>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-right text-zinc-100">${deal.listing_total.toFixed(2)}</td>
        <td className="px-3 py-2 text-right">
          <button
            onClick={() => setOpen((o) => !o)}
            className={`${open ? "text-zinc-100" : "text-zinc-400"} hover:text-white underline decoration-dotted underline-offset-2`}
            title="Audit the sold comps behind this median"
          >
            ${Math.round(deal.sold_median)}
            <span className="text-zinc-600 ml-1">{open ? "▾" : "▸"}</span>
          </button>
        </td>
        <td className={`px-3 py-2 text-right font-semibold ${edgeColor}`}>
          {deal.raw_gap_pct >= 0 ? "+" : ""}{deal.raw_gap_pct.toFixed(0)}%
        </td>
        <td className="px-3 py-2 text-center">
          <span className={`text-xs px-2 py-0.5 rounded ${liqColor}`}>{deal.liquidity}</span>
        </td>
        <td className="px-3 py-2 text-center text-xs">
          <div className="text-zinc-300">{deal.seller_name ?? "?"}</div>
          <div className="text-zinc-500">
            fb {fbStr}{deal.seller_joined_at ? ` · since ${deal.seller_joined_at}` : ""}
          </div>
        </td>
        <td className="px-3 py-2 text-right">
          <a
            href={deal.listing_url}
            target="_blank"
            rel="noopener"
            className="px-3 py-1 text-xs bg-zinc-100 text-zinc-900 rounded hover:bg-white"
          >
            BIN →
          </a>
        </td>
      </tr>
      {open && (
        <tr className="bg-zinc-950/80 border-b border-zinc-800">
          <td></td>
          <td colSpan={7} className="px-3 py-3">
            <div className="text-xs text-zinc-400 mb-2">
              <span className={`mr-2 px-1.5 py-0.5 rounded text-[10px] ${deal.sold_source === "ebay-market-data" ? "bg-emerald-900/50 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>
                {deal.sold_source === "ebay-market-data" ? "eBay market-data" : "SerpAPI sold"}
              </span>
              <span className="text-zinc-200">Median ${audit.median.toLocaleString()}</span> — {audit.method}.
              {" "}Rejected before scoring: {audit.rejected.wrong_grade} wrong-grade, {audit.rejected.foreign} non-English, {audit.rejected.junk} junk, {audit.rejected.outlier} price-outlier.
            </div>

            {/* Pricing insights */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <Metric label="last sold" value={deal.last_sold != null ? `$${deal.last_sold.toLocaleString()}` : "—"}
                sub={deal.sold_last_at ? new Date(deal.sold_last_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : undefined} />
              <Metric label="avg sold" value={deal.avg_sold != null ? `$${deal.avg_sold.toLocaleString()}` : "—"}
                sub={deal.sold_low != null && deal.sold_high != null ? `range $${Math.round(deal.sold_low)}–$${Math.round(deal.sold_high)}` : undefined} />
              <Metric
                label="trend (30d vs prior)"
                value={deal.trend_dir === "down" ? `▼ ${deal.trend_pct}%` : deal.trend_dir === "up" ? `▲ +${deal.trend_pct}%` : deal.trend_dir === "flat" ? "→ flat" : "—"}
                tone={deal.trend_dir === "down" ? "bad" : deal.trend_dir === "up" ? "good" : "neutral"}
              />
              <Metric
                label="est. net if flipped"
                value={deal.est_profit != null ? `${deal.est_profit >= 0 ? "+" : ""}$${deal.est_profit.toLocaleString()}` : "—"}
                sub={deal.net_after_fees != null ? `$${deal.net_after_fees.toLocaleString()} after ~13% fees` : undefined}
                tone={deal.est_profit != null && deal.est_profit > 0 ? "good" : deal.est_profit != null && deal.est_profit < 0 ? "bad" : "neutral"}
              />
            </div>
            <div className="text-xs text-zinc-600 mb-2">
              {deal.is_lowest_bin && deal.active_bins > 1
                ? `This is the cheapest of ${deal.active_bins} active BINs found for this card.`
                : deal.active_bins > 1
                ? `${deal.active_bins} active BINs found for this card.`
                : "Only active BIN found for this card."}
              {" "}"Est. net if flipped" = resell at median − ~13% eBay/payment fees − this BIN's cost.
            </div>
            {deal.pc_reference != null && (
              <div className={`text-xs mb-2 ${deal.pc_flag ? "text-amber-400" : "text-zinc-500"}`}>
                PriceCharting cross-check: {deal.grader ? `PSA ${deal.grade}` : ""} reference ${deal.pc_reference.toLocaleString()}
                {" "}({deal.pc_divergence_pct! >= 0 ? "+" : ""}{deal.pc_divergence_pct}% vs our median)
                {deal.pc_flag ? " — ⚠ divergence >40%, comp pool may mix variants; edge capped." : " — consistent."}
              </div>
            )}
            {audit.comps.length === 0 ? (
              <div className="text-zinc-500 text-xs italic">No gate-passing sold comps — median is a discovery-time estimate. Verify manually.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-zinc-600 uppercase">
                  <tr>
                    <th className="text-left py-1 pr-2">sold listing (same PSA grade)</th>
                    <th className="text-right py-1 px-2">sold $</th>
                    <th className="text-right py-1 px-2">when</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {audit.comps.map((c) => {
                    const above = c.total >= deal.listing_total;
                    return (
                      <tr key={c.item_id} className="border-t border-zinc-800/50">
                        <td className="py-1 pr-2 text-zinc-300 truncate max-w-md" title={c.title}>{c.title}</td>
                        <td className={`py-1 px-2 text-right ${above ? "text-emerald-400" : "text-zinc-500"}`}>${c.total.toFixed(2)}</td>
                        <td className="py-1 px-2 text-right text-zinc-500">{c.days_ago != null ? `${c.days_ago}d ago` : "?"}</td>
                        <td className="py-1 text-right">
                          <a href={c.url} target="_blank" rel="noopener" className="text-zinc-400 hover:text-white">view →</a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div className="text-xs text-zinc-600 mt-2">
              Green sold prices are at or above this BIN — i.e. comps that support the edge. This listing: ${deal.listing_total.toFixed(2)}.
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function PriceCheckBar() {
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PriceCheckResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function run(): Promise<void> {
    if (title.trim().length < 3) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.priceCheck(title.trim());
      setResult(r);
      setOpen(true);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mb-6">
      {/* min-w-0 on the input below is load-bearing: a flex child defaults to min-width:auto,
          so `flex-1` alone will not let it shrink below the intrinsic width of that long
          placeholder, and the row pushed the page sideways on a phone. */}
      <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center max-w-2xl">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void run(); }}
          placeholder='Card title, e.g. "Charizard 199/165 151 PSA 10" or "Luffy OP01-024 PSA 9"'
          className="flex-1 min-w-0 w-full sm:w-auto bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-400 outline-none"
        />
        <button
          onClick={() => void run()}
          disabled={loading || title.trim().length < 3}
          className="px-4 py-2 bg-zinc-100 text-zinc-900 font-mono text-sm rounded hover:bg-white disabled:opacity-40 whitespace-nowrap"
        >
          {loading ? "checking…" : "get live price"}
        </button>
      </div>
      <p className="text-xs text-zinc-600 mt-1 font-mono">
        Include a PSA grade in the title to gate comps to that grade (PSA 10 vs PSA 10). Same sold-comp engine as the deals below.
      </p>
      {err && <p className="text-red-400 text-xs font-mono mt-1">{err}</p>}

      {open && result && (
        <div
          className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-6 overflow-y-auto"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-zinc-950 border border-zinc-700 rounded-lg max-w-3xl w-full mt-12 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b border-zinc-800">
              <div>
                <div className="text-zinc-100 font-mono text-sm">{result.query}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  {result.detected_grade ? `gated to ${result.detected_grade}` : "no grade filter"} ·{" "}
                  {result.count} comps · {result.count_30d} in last 30d
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200 text-lg leading-none">×</button>
            </div>

            <div className="p-4 border-b border-zinc-800 flex gap-8">
              <div>
                <div className="text-3xl font-mono text-emerald-400">
                  {result.median != null ? `$${result.median.toLocaleString()}` : "—"}
                </div>
                <div className="text-xs uppercase text-zinc-500 tracking-wider mt-1">market value (median)</div>
              </div>
              {result.low != null && result.high != null && (
                <div className="self-center text-sm font-mono text-zinc-400">
                  range ${Math.round(result.low).toLocaleString()} – ${Math.round(result.high).toLocaleString()}
                </div>
              )}
            </div>

            <div className="p-4">
              <div className="text-xs text-zinc-500 mb-2">{result.note}</div>
              {result.comps.length === 0 ? (
                <div className="text-zinc-500 text-sm italic py-6 text-center">
                  No matching sold listings. Check the spelling / card number, or remove the grade to broaden.
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs font-mono">
                    <thead className="text-zinc-600 uppercase sticky top-0 bg-zinc-950">
                      <tr>
                        <th className="text-left py-1 pr-2">sold listing</th>
                        <th className="text-right py-1 px-2">sold $</th>
                        <th className="text-right py-1 px-2">when</th>
                        <th className="w-14"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.comps.map((c) => (
                        <tr key={c.item_id} className="border-t border-zinc-800/60">
                          <td className="py-1 pr-2 text-zinc-300 truncate max-w-md" title={c.title}>{c.title}</td>
                          <td className="py-1 px-2 text-right text-zinc-100">${c.total.toFixed(2)}</td>
                          <td className="py-1 px-2 text-right text-zinc-500">{c.days_ago != null ? `${c.days_ago}d` : "?"}</td>
                          <td className="py-1 text-right">
                            <a href={c.url} target="_blank" rel="noopener" className="text-zinc-400 hover:text-white">view →</a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
